import React, { useEffect, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles } from 'lucide-react';
import { clientApi } from '@/lib/adminApi';

const STATUS_TONE = {
  pending: 'bg-warning/10 text-warning border-warning/30',
  approved: 'bg-success/10 text-success border-success/30',
  rejected: 'bg-destructive/10 text-destructive border-destructive/30',
};

// Founder audit (Aug 2026) — clients previously had no way to track their
// strategic analysis requests after submitting. Mounted in Climara + TI.
export const StrategicRequestsHistory = ({ refresh }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (refresh) return;
    clientApi.listStrategicAnalysisRequests()
      .then((d) => setItems(d?.items || []))
      .catch(() => setItems([]));
  }, [refresh]);

  if (items.length === 0) return null;

  return (
    <Card className="p-4 shadow-card" data-testid="strategic-requests-history">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-sm">
          {t('strategicHistory.title', { defaultValue: 'Strategic analysis requests' })}
        </h3>
        <Badge variant="outline" className="text-xs tabular-nums">{items.length}</Badge>
      </div>
      <ul className="space-y-2">
        {items.slice(0, 6).map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border/70 px-3 py-2" data-testid={`strategic-history-row-${r.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {t(`clientTalentIntelligence.strategicDialog.areas.${r.area}`, { defaultValue: r.area })}
                <span className="text-muted-foreground font-normal">
                  {' · '}{t(`clientTalentIntelligence.strategicDialog.periods.${r.period}`, { defaultValue: r.period })}
                </span>
              </p>
              <p className="text-xs text-muted-foreground truncate" title={r.description}>
                {new Date(r.created_at).toLocaleDateString(getUiLocale())} · {r.description}
              </p>
              {r.status === 'rejected' && r.reject_reason && (
                <p className="text-xs text-destructive mt-0.5" data-testid={`strategic-history-reason-${r.id}`}>
                  {t('strategicHistory.rejectReason', { defaultValue: 'Reason' })}: {r.reject_reason}
                </p>
              )}
            </div>
            <Badge variant="outline" className={`text-xs ${STATUS_TONE[r.status] || ''}`} data-testid={`strategic-history-status-${r.id}`}>
              {t(`strategicHistory.status.${r.status}`, { defaultValue: r.status })}
            </Badge>
          </li>
        ))}
      </ul>
    </Card>
  );
};

export default StrategicRequestsHistory;
